import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { User, MapPin, Star, Calendar, Edit2, Save, X, Award, Building2, Waves, Fish, Camera, Eye, EyeOff, Lock, Check, Send, Trash2 } from "lucide-react";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingUserMenu from "@/components/FloatingUserMenu";
import ImageLightbox from "@/components/ImageLightbox";
import SEO from "@/components/SEO";
import ProProfileTab from "@/components/ProProfileTab";
import DiveCenterProfileTab from "@/components/DiveCenterProfileTab";
import DiveReportModal from "@/components/DiveReportModal";
import { getImageUrl } from "@/utils/imageUtils";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const Profile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const sharedReportsRef = useRef(null);
  const [user, setUser] = useState(null);
  const [reports, setReports] = useState([]);
  const [diveReports, setDiveReports] = useState([]); // New dive reports
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activeFilter, setActiveFilter] = useState('dive_reports'); // 'dive_reports', 'reports', 'species', 'reviews'
  const [activeTab, setActiveTab] = useState('profile'); // 'profile', 'pro', 'dive_center'
  const [editData, setEditData] = useState({});
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [userBadges, setUserBadges] = useState([]);
  const [selectedDiveReport, setSelectedDiveReport] = useState(null);
  const [diveReportModalOpen, setDiveReportModalOpen] = useState(false);
  const [identifiedSpecies, setIdentifiedSpecies] = useState([]);
  const [pendingShares, setPendingShares] = useState([]);

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        // Get token and current user from localStorage
        const token = localStorage.getItem("token");
        const currentUser = JSON.parse(localStorage.getItem("user") || "null");
        
        let userData = null;
        
        // If viewing own profile, use data from localStorage
        if (currentUser && currentUser.id === userId) {
          userData = currentUser;
        } else {
          // Fetch public profile data for any user
          try {
            const profileResponse = await axios.get(`${API}/users/${userId}/profile`, {
              headers: token ? { Authorization: `Bearer ${token}` } : {}
            });
            userData = profileResponse.data;
          } catch (apiError) {
            if (apiError.response?.status === 404) {
              toast.error("User not found");
            } else {
              toast.error("Failed to load profile");
            }
            navigate('/');
            return;
          }
        }
        
        if (!userData) {
          toast.error("User not found");
          navigate('/');
          return;
        }
        
        setUser(userData);
        setEditData(userData);
        setIsOwnProfile(currentUser && currentUser.id === userId);

        // Fetch user's new dive reports
        try {
          const diveReportsResponse = await axios.get(`${API}/dive-reports/user/${userId}`);
          setDiveReports(diveReportsResponse.data);
        } catch (err) {
          console.error("Error fetching dive reports:", err);
        }

        // Fetch pending shared reports (own profile only)
        if (currentUser && currentUser.id === userId && token) {
          try {
            const sharesRes = await axios.get(`${API}/dive-reports/shared/pending`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setPendingShares(sharesRes.data);
          } catch (err) {
            console.error("Error fetching shared reports:", err);
          }
        }

        // Fetch user's legacy dive site reports
        const reportsResponse = await axios.get(`${API}/dive-site-reports/user/${userId}`);
        setReports(reportsResponse.data);

        // Fetch user's identified species (from marine life submissions)
        try {
          const identifiedResponse = await axios.get(`${API}/marine-database/user-identifications/${userId}`);
          setIdentifiedSpecies(identifiedResponse.data);
        } catch (err) {
          console.error("Error fetching identified species:", err);
        }

        // Fetch user's reviews
        try {
          const reviewsResponse = await axios.get(`${API}/reviews/all`);
          const userReviews = reviewsResponse.data.filter(r => r.user_id === userId);
          setReviews(userReviews);
        } catch (err) {
          console.error("Error fetching reviews:", err);
        }

        // Fetch user's badges
        try {
          const badgesResponse = await axios.get(`${API}/users/${userId}/badges`);
          setUserBadges(badgesResponse.data);
        } catch (err) {
          console.error("Error fetching badges:", err);
        }
      } catch (error) {
        console.error("Error loading profile:", error);
        toast.error("Failed to load profile");
      } finally {
        setLoading(false);
      }
    };

    fetchProfileData();
  }, [userId, navigate, location]);

  // Scroll to shared reports section when navigated from notification
  useEffect(() => {
    const section = searchParams.get("section");
    if (section === "shared" && !loading && pendingShares.length > 0) {
      setActiveFilter('dive_reports');
      setTimeout(() => {
        sharedReportsRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
    }
  }, [searchParams, loading, pendingShares]);

  const handleSaveProfile = async () => {
    try {
      const token = localStorage.getItem("token");
      
      if (!token) {
        toast.error("You must be logged in to update your profile");
        return;
      }

      // For dive_center accounts, only allow updating name, username, and profile_pic
      const updateData = user.role === 'dive_center' 
        ? {
            name: editData.name || undefined,
            username: editData.username || undefined,
            profile_pic: editData.profile_pic || undefined
          }
        : {
            name: editData.name || undefined,
            username: editData.username || undefined,
            country: editData.country || undefined,
            diving_level: editData.diving_level || undefined,
            age: editData.age ? parseInt(editData.age) : undefined,
            age_visible: editData.age_visible,
            profile_pic: editData.profile_pic || undefined
          };

      const response = await axios.put(`${API}/users/me`, updateData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUser(response.data);
      setEditData(response.data);
      localStorage.setItem("user", JSON.stringify(response.data));
      
      // Trigger event for FloatingUserMenu to refresh
      window.dispatchEvent(new Event('userLogin'));
      
      setIsEditing(false);
      toast.success("Profile updated successfully!");
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error(error.response?.data?.detail || "Failed to update profile");
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${
              star <= rating
                ? "fill-yellow-400 text-yellow-400"
                : "text-gray-400"
            }`}
          />
        ))}
      </div>
    );
  };

  const handleDeleteReport = async (e, reportId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this dive report? This cannot be undone.")) return;
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("token");
      await axios.delete(`${API}/dive-reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDiveReports(prev => prev.filter(r => r.id !== reportId));
      toast.success("Dive report deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete report");
    }
  };

  const DIVING_LEVELS = [
    "Beginner / Not Certified",
    "Open Water Diver",
    "Advanced Open Water",
    "Rescue Diver",
    "Divemaster",
    "Instructor"
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-blue-900 flex items-center justify-center">
        <div className="text-white text-xl">Loading profile...</div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen overflow-x-hidden">
      <SEO 
        title={`${user.name}'s Profile | George Dives`}
        description={`View ${user.name}'s dive site reports and marine life observations`}
      />
      
      <Navigation />

      {/* Header Section - matches Account page style */}
      <section className="pt-32 pb-12 bg-gradient-to-br from-cyan-500 to-blue-600">
        <div className="container mx-auto px-4 max-w-4xl text-center">
          <div className="flex justify-center mb-6">
            <div className={`w-24 h-24 rounded-full shadow-lg flex items-center justify-center overflow-hidden ring-4 ring-white/20 ${user.role === 'dive_center' ? 'bg-gradient-to-br from-cyan-500 to-blue-600 p-2' : 'bg-gradient-to-br from-cyan-400 to-blue-500'}`}>
              {user.profile_pic ? (
                <img 
                  src={getImageUrl(user.profile_pic)} 
                  alt={user.name} 
                  className={`object-cover ${user.role === 'dive_center' ? 'w-full h-full rounded-full' : 'w-full h-full'}`}
                />
              ) : (
                <User className="w-12 h-12 text-white" />
              )}
            </div>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
            {user.username && <span className="text-cyan-100">@{user.username}</span>}
            {user.username && <span className="mx-2">•</span>}
            {user.name}
          </h1>
          <p className="text-lg text-white/90 mb-4">
            {user.role === 'member' ? 'Community Member' : user.role.charAt(0).toUpperCase() + user.role.slice(1)}
          </p>
          
          {/* User Badges */}
          {userBadges.length > 0 && (
            <div className="flex flex-wrap justify-center gap-2">
              {userBadges.map((badge) => (
                <div
                  key={badge.id}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${badge.color || 'bg-yellow-100 text-yellow-700'}`}
                  title={badge.description}
                >
                  <span>{badge.icon}</span>
                  <span className="font-medium">{badge.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          
          {/* Profile Info Card */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex justify-between items-start">
                <CardTitle className="text-xl">Profile Details</CardTitle>
                {isOwnProfile && !isEditing && (
                  <Button
                    onClick={() => setIsEditing(true)}
                    className="bg-cyan-500 hover:bg-cyan-600"
                    size="sm"
                  >
                    <Edit2 className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                )}
                {isOwnProfile && isEditing && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleSaveProfile}
                      className="bg-green-500 hover:bg-green-600"
                      size="sm"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Save
                    </Button>
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setEditData(user);
                      }}
                      variant="outline"
                      size="sm"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {isEditing ? (
                <div className="space-y-4">
                  {/* Show notice for dive center accounts */}
                  {user.role === 'dive_center' && (
                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                      <div className="flex items-center gap-2 text-blue-700">
                        <Lock className="w-4 h-4" />
                        <span className="text-sm font-medium">Dive Center Account</span>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        Some fields are managed through your Dive Center Profile tab. Edit your dive center&apos;s public information there.
                      </p>
                    </div>
                  )}
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Full Name</Label>
                      <Input
                        value={editData.name || ''}
                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Username</Label>
                      <Input
                        value={editData.username || ''}
                        onChange={(e) => setEditData({...editData, username: e.target.value})}
                      />
                    </div>
                    
                    {/* Country - locked for dive_center */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Country
                        {user.role === 'dive_center' && <Lock className="w-3 h-3 text-gray-400" />}
                      </Label>
                      <Input
                        value={editData.country || ''}
                        onChange={(e) => setEditData({...editData, country: e.target.value})}
                        disabled={user.role === 'dive_center'}
                        className={user.role === 'dive_center' ? 'bg-gray-100 cursor-not-allowed' : ''}
                      />
                    </div>
                    
                    {/* Age - locked for dive_center */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Age
                        {user.role === 'dive_center' && <Lock className="w-3 h-3 text-gray-400" />}
                      </Label>
                      <Input
                        type="number"
                        value={editData.age || ''}
                        onChange={(e) => setEditData({...editData, age: e.target.value})}
                        disabled={user.role === 'dive_center'}
                        className={user.role === 'dive_center' ? 'bg-gray-100 cursor-not-allowed' : ''}
                      />
                    </div>
                    
                    {/* Age Visibility Toggle - only for non-dive_center */}
                    {user.role !== 'dive_center' && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          {editData.age_visible !== false ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                          Age Visibility
                        </Label>
                        <div className="flex items-center gap-3 p-3 border rounded-lg">
                          <Switch
                            checked={editData.age_visible !== false}
                            onCheckedChange={(checked) => setEditData({...editData, age_visible: checked})}
                          />
                          <span className="text-sm text-gray-600">
                            {editData.age_visible !== false ? 'Age is visible to others' : 'Age is hidden from others'}
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Diving Level - locked for dive_center */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Diving Level
                        {user.role === 'dive_center' && <Lock className="w-3 h-3 text-gray-400" />}
                      </Label>
                      {user.role === 'dive_center' ? (
                        <Input
                          value={editData.diving_level || ''}
                          disabled
                          className="bg-gray-100 cursor-not-allowed"
                        />
                      ) : (
                        <Select
                          value={editData.diving_level || ''}
                          onValueChange={(value) => setEditData({...editData, diving_level: value})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select level..." />
                          </SelectTrigger>
                          <SelectContent>
                            {DIVING_LEVELS.map((level) => (
                              <SelectItem key={level} value={level}>
                                {level}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    
                    {/* Profile Picture - available for all but notice for dive_center */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        Profile Picture
                        {user.role === 'dive_center' && (
                          <span className="text-xs text-gray-500">(Use Dive Center logo instead)</span>
                        )}
                      </Label>
                      {user.role !== 'dive_center' ? (
                        <>
                          <Input
                            type="file"
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
                            data-testid="profile-pic-upload-input"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              
                              const reader = new FileReader();
                              reader.onload = (event) => {
                                setEditData(prev => ({...prev, profile_pic_preview: event.target.result}));
                              };
                              reader.readAsDataURL(file);
                              
                              try {
                                const formData = new FormData();
                                formData.append('file', file);
                                
                                const token = localStorage.getItem("token");
                                const response = await axios.post(`${API}/upload/profile-picture`, formData, {
                                  headers: { 
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'multipart/form-data'
                                  }
                                });
                                
                                setEditData(prev => ({...prev, profile_pic: response.data.image_url}));
                                toast.success("Profile picture uploaded! Click 'Save' to update your profile.");
                              } catch (error) {
                                console.error("Error uploading image:", error);
                                toast.error(error.response?.data?.detail || "Failed to upload image");
                              }
                            }}
                          />
                          {editData.profile_pic_preview && (
                            <div className="mt-2">
                              <img 
                                src={editData.profile_pic_preview} 
                                alt="Preview" 
                                className="w-24 h-24 rounded-full object-cover border-2 border-cyan-400"
                              />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="p-3 bg-gray-50 rounded-lg text-sm text-gray-500">
                          Update your logo in the Dive Center Profile tab
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Country - hide for dive_center */}
                  {user.country && user.role !== 'dive_center' && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <MapPin className="w-5 h-5 text-cyan-600" />
                      <div>
                        <p className="text-sm text-gray-600">Country</p>
                        <p className="font-semibold">{user.country}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Diving Level - hide for dive_center */}
                  {user.diving_level && user.role !== 'dive_center' && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <Award className="w-5 h-5 text-cyan-600" />
                      <div>
                        <p className="text-sm text-gray-600">Diving Level</p>
                        <p className="font-semibold">{user.diving_level}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Age - respect visibility setting, hide for dive_center */}
                  {user.age && user.role !== 'dive_center' && (isOwnProfile || user.age_visible !== false) && (
                    <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                      <User className="w-5 h-5 text-cyan-600" />
                      <div>
                        <p className="text-sm text-gray-600">
                          Age
                          {isOwnProfile && user.age_visible === false && (
                            <span className="ml-2 text-xs text-amber-600">(Hidden from others)</span>
                          )}
                        </p>
                        <p className="font-semibold">{user.age}</p>
                      </div>
                    </div>
                  )}
                  
                  {/* Empty state - different for dive_center */}
                  {user.role === 'dive_center' ? (
                    !user.currently_in && (
                      <p className="text-gray-500 text-center py-4">
                        Manage your dive center information in the Dive Center Profile tab
                      </p>
                    )
                  ) : (
                    !user.country && !user.diving_level && !user.age && !user.currently_in && (
                      <p className="text-gray-500 text-center py-4">No profile details added yet</p>
                    )
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Profile Tabs - Only show for own profile with special roles */}
          {isOwnProfile && (user.role === 'pro' || user.role === 'dive_center' || user.role === 'admin') && (
            <div className="flex gap-2 mb-6 flex-wrap">
              <Button
                variant={activeTab === 'profile' ? "default" : "outline"}
                onClick={() => setActiveTab('profile')}
                className={activeTab === 'profile' ? "bg-cyan-600" : ""}
              >
                <User className="w-4 h-4 mr-2" />
                Profile
              </Button>
              {(user.role === 'pro' || user.role === 'admin') && (
                <Button
                  variant={activeTab === 'pro' ? "default" : "outline"}
                  onClick={() => setActiveTab('pro')}
                  className={activeTab === 'pro' ? "bg-cyan-600" : ""}
                >
                  <Award className="w-4 h-4 mr-2" />
                  Pro Profile
                </Button>
              )}
              {user.role === 'dive_center' && (
                <Button
                  variant={activeTab === 'dive_center' ? "default" : "outline"}
                  onClick={() => setActiveTab('dive_center')}
                  className={activeTab === 'dive_center' ? "bg-blue-600" : ""}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Dive Center Profile
                </Button>
              )}
            </div>
          )}

          {/* Pro Profile Tab Content */}
          {isOwnProfile && activeTab === 'pro' && (user.role === 'pro' || user.role === 'admin') && (
            <div className="mb-6">
              <ProProfileTab 
                user={user}
                token={localStorage.getItem("token")}
                onUpdate={(updatedUser) => {
                  setUser(updatedUser);
                  localStorage.setItem("user", JSON.stringify(updatedUser));
                }}
              />
            </div>
          )}

          {/* Dive Center Profile Tab Content */}
          {isOwnProfile && activeTab === 'dive_center' && user.role === 'dive_center' && (
            <div className="mb-6">
              <DiveCenterProfileTab 
                user={user}
                token={localStorage.getItem("token")}
                onUpdate={(updatedUser) => {
                  setUser(updatedUser);
                  localStorage.setItem("user", JSON.stringify(updatedUser));
                }}
              />
            </div>
          )}

          {/* Only show stats and reports when on the main profile tab */}
          {activeTab === 'profile' && (
            <>
              {/* Stats Cards - Clickable */}
              {/* Stats Cards - Hide for dive_center accounts */}
              {user.role !== 'dive_center' && (
                <div className="grid grid-cols-3 gap-4 mb-8">
                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 ${activeFilter === 'dive_reports' ? 'ring-2 ring-blue-500' : ''}`}
                    onClick={() => setActiveFilter('dive_reports')}
                  >
                    <CardHeader className="text-center py-4">
                      <Waves className="w-6 h-6 mx-auto mb-2 text-blue-600" />
                      <CardTitle className="text-3xl font-bold text-blue-600">{diveReports.length}</CardTitle>
                      <CardDescription className="text-xs">Dive Reports</CardDescription>
                    </CardHeader>
                  </Card>

                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 ${activeFilter === 'species' ? 'ring-2 ring-green-500' : ''}`}
                    onClick={() => setActiveFilter('species')}
                  >
                    <CardHeader className="text-center py-4">
                      <Fish className="w-6 h-6 mx-auto mb-2 text-green-600" />
                      <CardTitle className="text-3xl font-bold text-green-600">
                        {identifiedSpecies.length}
                      </CardTitle>
                      <CardDescription className="text-xs">Species IDs</CardDescription>
                    </CardHeader>
                  </Card>

                  <Card 
                    className={`cursor-pointer transition-all hover:scale-105 ${activeFilter === 'reviews' ? 'ring-2 ring-yellow-500' : ''}`}
                    onClick={() => setActiveFilter('reviews')}
                  >
                    <CardHeader className="text-center py-4">
                      <Star className="w-6 h-6 mx-auto mb-2 text-yellow-600" />
                      <CardTitle className="text-3xl font-bold text-yellow-600">
                        {reviews.length}
                      </CardTitle>
                      <CardDescription className="text-xs">Reviews</CardDescription>
                    </CardHeader>
                  </Card>
                </div>
              )}

              {/* Content Area - Filtered by clicked stat - Hide for dive_center accounts */}
              {user.role !== 'dive_center' && (
              <div className="mb-8">
                {/* New Dive Reports */}
                {activeFilter === 'dive_reports' && (
                  <>
                    <div className="flex justify-between items-center mb-4">
                      <h2 className="text-2xl font-bold text-gray-900">Dive Reports</h2>
                      {isOwnProfile && (
                        <Button 
                          onClick={() => navigate('/dive-report')}
                          className="bg-blue-600 hover:bg-blue-700"
                        >
                          <Waves className="w-4 h-4 mr-2" />
                          New Dive Report
                        </Button>
                      )}
                    </div>
                    {/* Pending Shared Reports */}
                    {isOwnProfile && pendingShares.length > 0 && (
                      <div ref={sharedReportsRef} className="mb-4 space-y-2">
                        {pendingShares.map(share => (
                          <Card key={share.id} className="border-blue-200 bg-blue-50/50">
                            <CardContent className="py-3 px-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Send className="w-5 h-5 text-blue-500" />
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {share.sender_name} shared a report with you
                                    </p>
                                    <p className="text-xs text-gray-500">"{share.report_title}"</p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-blue-600 border-blue-300 hover:bg-blue-50"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
                                        const res = await axios.get(`${API}/dive-reports/${share.report_id}`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        setSelectedDiveReport(res.data);
                                        setDiveReportModalOpen(true);
                                      } catch {
                                        toast.error("Could not load report preview");
                                      }
                                    }}
                                    data-testid={`preview-share-${share.id}`}
                                  >
                                    <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                                  </Button>
                                  <Button
                                    size="sm"
                                    className="bg-blue-500 hover:bg-blue-600 h-8"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
                                        await axios.post(`${API}/dive-reports/shared/${share.id}/accept`, {}, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        toast.success("Report copied to your dive log!");
                                        setPendingShares(prev => prev.filter(s => s.id !== share.id));
                                        // Refresh dive reports
                                        const res = await axios.get(`${API}/dive-reports/user/${userId}`);
                                        setDiveReports(res.data);
                                      } catch (err) {
                                        toast.error(err.response?.data?.detail || "Failed to accept");
                                      }
                                    }}
                                    data-testid={`accept-share-${share.id}`}
                                  >
                                    <Check className="w-3.5 h-3.5 mr-1" /> Accept
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-8 text-gray-500"
                                    onClick={async () => {
                                      try {
                                        const token = localStorage.getItem("token") || sessionStorage.getItem("token");
                                        await axios.delete(`${API}/dive-reports/shared/${share.id}`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        setPendingShares(prev => prev.filter(s => s.id !== share.id));
                                        toast.success("Share declined");
                                      } catch (err) {
                                        toast.error("Failed to decline");
                                      }
                                    }}
                                    data-testid={`decline-share-${share.id}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                    {diveReports.length === 0 ? (
                      <Card>
                        <CardHeader className="text-center">
                          <CardTitle className="text-xl">No Dive Reports Yet</CardTitle>
                          <CardDescription>
                            {isOwnProfile ? "Start logging your dives to track your underwater adventures" : "This user hasn't logged any dives yet"}
                          </CardDescription>
                          {isOwnProfile && (
                            <Button 
                              onClick={() => navigate('/dive-report')}
                              className="mt-4 bg-blue-600 hover:bg-blue-700"
                            >
                              Create Your First Dive Report
                            </Button>
                          )}
                        </CardHeader>
                      </Card>
                    ) : (
                      <div className="space-y-3">
                        {diveReports.map((report) => (
                          <Card 
                            key={report.id} 
                            className="cursor-pointer hover:shadow-md transition-shadow"
                            onClick={() => {
                              setSelectedDiveReport(report);
                              setDiveReportModalOpen(true);
                            }}
                          >
                            <CardContent className="py-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                  <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full flex items-center justify-center">
                                    <Waves className="w-6 h-6 text-white" />
                                  </div>
                                  <div>
                                    <h3 className="font-semibold text-gray-900">{report.title}</h3>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                      <Calendar className="w-4 h-4" />
                                      {new Date(report.dive_date).toLocaleDateString('en-US', {
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric'
                                      })}
                                      {report.dive_site_name && (
                                        <>
                                          <span>•</span>
                                          <MapPin className="w-4 h-4" />
                                          {report.dive_site_name}
                                        </>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center gap-4 text-sm">
                                  {report.depth && (
                                    <span className="text-gray-600">
                                      {report.depth}{report.depth_unit || 'm'}
                                    </span>
                                  )}
                                  {report.dive_time && (
                                    <span className="text-gray-600">
                                      {report.dive_time}min
                                    </span>
                                  )}
                                  {report.rating && (
                                    <div className="flex">
                                      {[1,2,3,4,5].map(s => (
                                        <Star key={s} className={`w-4 h-4 ${s <= report.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                                      ))}
                                    </div>
                                  )}
                                  {isOwnProfile && (
                                    <button
                                      data-testid={`delete-report-${report.id}`}
                                      onClick={(e) => handleDeleteReport(e, report.id)}
                                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                      title="Delete report"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* Species Identified */}
                {activeFilter === 'species' && (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">Species Identified</h2>
                    {identifiedSpecies.length === 0 ? (
                      <Card>
                        <CardHeader className="text-center">
                          <CardTitle className="text-xl">No Species IDs Yet</CardTitle>
                          <CardDescription>
                            Submit species ID requests when you&apos;re unsure about a marine creature you&apos;ve spotted
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    ) : (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {identifiedSpecies.map((item) => {
                          const speciesName = item.species_info?.common_names?.[0] || "Unknown Species";
                          const scientificName = item.species_info?.scientific_name;
                          const imageUrl = item.image_urls?.[0];
                          
                          return (
                            <Card 
                              key={item.id} 
                              className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                              onClick={() => {
                                if (item.image_urls?.length > 0) {
                                  setLightboxImages(item.image_urls.map(url => 
                                    url.startsWith('http') ? url : `${BACKEND_URL}${url}`
                                  ));
                                  setLightboxIndex(0);
                                  setLightboxOpen(true);
                                }
                              }}
                            >
                              {/* Photo */}
                              <div className="relative h-40 bg-gradient-to-br from-cyan-100 to-blue-100">
                                {imageUrl ? (
                                  <img 
                                    src={imageUrl.startsWith('http') ? imageUrl : `${BACKEND_URL}${imageUrl}`}
                                    alt={speciesName}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <Camera className="w-12 h-12 text-cyan-300" />
                                  </div>
                                )}
                                {/* Category badge */}
                                {item.species_info?.simple_category && (
                                  <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 rounded-full text-white text-xs">
                                    {item.species_info.simple_category}
                                  </div>
                                )}
                              </div>
                              
                              {/* Info */}
                              <CardContent className="p-4">
                                <h3 className="font-semibold text-gray-900 text-lg">{speciesName}</h3>
                                {scientificName && (
                                  <p className="text-sm text-gray-500 italic">{scientificName}</p>
                                )}
                                <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
                                  <MapPin className="w-3 h-3" />
                                  <span>{item.dive_site_name || item.country || "Unknown location"}</span>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                                  <Calendar className="w-3 h-3" />
                                  <span>
                                    {item.reviewed_at 
                                      ? new Date(item.reviewed_at).toLocaleDateString('en-US', { 
                                          year: 'numeric', 
                                          month: 'short', 
                                          day: 'numeric' 
                                        })
                                      : "Recently identified"
                                    }
                                  </span>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}

                {/* Reviews */}
                {activeFilter === 'reviews' && (
                  <>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">My Reviews</h2>
                    {reviews.length === 0 ? (
                      <Card>
                        <CardHeader className="text-center">
                          <CardTitle className="text-xl">No Reviews Yet</CardTitle>
                          <CardDescription>
                            Share your diving experiences by submitting reviews
                          </CardDescription>
                        </CardHeader>
                      </Card>
                    ) : (
                      <div className="space-y-4">
                        {reviews.map((review) => (
                          <Card key={review.id}>
                            <CardHeader>
                              <div className="flex justify-between items-start">
                                <div>
                                  <CardTitle className="text-xl">{review.dive_center}</CardTitle>
                                  <CardDescription>
                                    {review.country_dove} • {review.program}
                                  </CardDescription>
                                </div>
                                <div className="flex gap-1">
                                  {[1, 2, 3, 4, 5].map((star) => (
                                    <Star
                                      key={star}
                                      className={`w-4 h-4 ${
                                        star <= review.rating
                                          ? "fill-yellow-400 text-yellow-400"
                                          : "text-gray-300"
                                      }`}
                                    />
                                  ))}
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <p className="text-gray-700">{review.review_text}</p>
                              
                              {(() => {
                                const images = review.image_urls && review.image_urls.length > 0 
                                  ? review.image_urls 
                                  : review.image_url ? [review.image_url] : [];
                                
                                if (images.length === 0) return null;
                                
                                return (
                                  <div className="mt-4">
                                    <div className="flex gap-2 overflow-x-auto">
                                      {images.map((imgUrl, idx) => (
                                        <img 
                                          key={idx}
                                          src={`${BACKEND_URL}${imgUrl}`}
                                          alt={`Photo ${idx + 1}`}
                                          className="h-32 w-32 rounded-lg object-cover cursor-pointer hover:opacity-90 hover:scale-105 transition-all border-2 border-cyan-200 flex-shrink-0"
                                          onClick={() => {
                                            setLightboxImages(images.map(url => `${BACKEND_URL}${url}`));
                                            setLightboxIndex(idx);
                                            setLightboxOpen(true);
                                          }}
                                          onError={(e) => {
                                            e.target.style.display = 'none';
                                          }}
                                        />
                                      ))}
                                    </div>
                                    <p className="text-xs text-gray-500 mt-1">
                                      {images.length > 1 ? `${images.length} photos - ` : ''}Click to view full size
                                    </p>
                                  </div>
                                );
                              })()}
                              
                              <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {new Date(review.created_at).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>
                                <div className={`px-2 py-1 rounded ${review.is_approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                  {review.is_approved ? '✓ Published' : '⏳ Pending Approval'}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              )}
            </>
          )}
        </div>
      </section>
      
      <FloatingUserMenu />
      
      <ImageLightbox
        images={lightboxImages}
        initialIndex={lightboxIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
      />
      
      <DiveReportModal
        report={selectedDiveReport}
        isOpen={diveReportModalOpen}
        onClose={() => {
          setDiveReportModalOpen(false);
          setSelectedDiveReport(null);
        }}
      />
      <Footer />

    </div>
  );
};

export default Profile;
